import React from 'react';
import { formatBusinessMonth, formatDate, formatDateLatn } from '../../../utils/dateFormatter';
import { formatSARGrouped } from '../../../utils/currencyFormatter';
import { getPlatformDisplayName } from '../../../utils/platformUtils';
// Arabic nominal-form rule (1 singular / 2 dual / 3–10 plural / 11+ singular).
// Reused rather than re-implemented — it is pure grammar, not alert logic, and a
// second copy would be a second place for the rule to drift.
import { arabicPlural } from '../../../utils/alertDisplay';
import { formatNumber, formatPercent, UnitCode } from './reportFormat';
import RevenueTrendChart from './RevenueTrendChart';
import PropertyLeagueTable from './PropertyLeagueTable';

/**
 * One monthly owner report, rendered from the FROZEN snapshot payload.
 *
 * Reads `report.payload` and nothing else — no live analytics, no derived
 * figures. Anything not in the snapshot is not shown, deliberately: this markup
 * becomes the PDF template unchanged (Phase 4), and a number computed in the
 * renderer could not be reproduced from the stored document.
 *
 * Structure is semantic and one block per property so print pagination can be
 * added later without a rewrite. Pagination itself is NOT attempted yet.
 *
 * Number conventions (see currencyFormatter.formatSARGrouped): money and counts
 * in Western digits, matching the /finance dashboard beside it and the backend's
 * own Arabic commentary. Dates go through dateFormatter, which is Arabic-Indic.
 */

/**
 * The night breakdown that makes an occupancy percentage readable.
 *
 * Three buckets that must always sum to the total: sold, closed to sale, and open
 * but unsold. "محظورة" matches the word the property calendar already uses for a
 * stop-sold day, so the report is not inventing vocabulary.
 *
 * The closed-to-sale figure is shown ONLY when the snapshot says the availability
 * ledger covered the whole month (`blockedNightsKnown`). Otherwise the count is a
 * lower bound, and printing "0 محظورة" when the real answer is "we have no data
 * for that month" is worse than not splitting at all — so it falls back to the
 * two-bucket form.
 *
 * `unitCount` is stated only when a property record sells more than one unit,
 * because the night total is unit-nights: 60 nights in a 30-day month is correct
 * for two units and looks like a bug without the explanation. The portfolio roll-up
 * has no unit count (the analyzer does not compute one), so it passes null and the
 * line is simply omitted rather than derived here.
 */
const NightBreakdown = ({ occupancy }) => {
  const total = occupancy?.totalNights || 0;
  if (!total) return null;

  const booked = occupancy?.bookedNights || 0;
  const units = occupancy?.unitCount;

  // Clamped against the total: the ledger and the bookings are two sources, and a
  // disagreement must not produce a negative bucket on an owner's report.
  const showBlocked = occupancy?.blockedNightsKnown === true;
  const blocked = showBlocked
    ? Math.min(Math.max(occupancy?.blockedNights || 0, 0), Math.max(total - booked, 0))
    : 0;
  const unsold = Math.max(total - booked - blocked, 0);

  return (
    <small className="report-nights">
      {formatNumber(total)} {arabicPlural(total, 'ليلة', 'ليلتان', 'ليال')}
      {units > 1 ? ` (${formatNumber(units)} ${arabicPlural(units, 'وحدة', 'وحدتان', 'وحدات')})` : ''}
      {' · '}{formatNumber(booked)} محجوزة
      {showBlocked && blocked > 0 ? ` · ${formatNumber(blocked)} محظورة` : ''}
      {' · '}{formatNumber(unsold)} غير مباعة
    </small>
  );
};

/**
 * Partial-month label for a property that went live inside the reported month.
 *
 * Attached to the occupancy figure, never a footnote — an owner reading a low
 * percentage must see the reason in the same glance. The number itself is NOT
 * rescored: the ops performance dashboard set that precedent deliberately, since
 * rescoring would describe onboarding timing rather than performance.
 */
const PartialMonthNote = ({ coverage, latnDates = false }) => {
  if (!coverage?.partialMonth) return null;

  // The document keeps one numeral system throughout; the screen keeps the digits
  // it has always shown. See the `variant` note on MonthlyReportView.
  const fmt = latnDates ? formatDateLatn : formatDate;

  return (
    <span className="report-partial-note">
      <i className="bi bi-info-circle" aria-hidden="true"></i>
      عقار مُضاف خلال الشهر
      {coverage.activatedOn ? ` · بدأ في ${fmt(coverage.activatedOn)}` : ''}
    </span>
  );
};

/**
 * The booking-performance figures. Commission, net and payout are absent by design.
 *
 * Month-over-month movement was removed from these cards (Adil, 2026-08-01): a single
 * percentage against one prior month reads as a trend when it is not — one late booking
 * swings it, and the 6-month chart below already answers the direction question honestly.
 * The comparison is still on every snapshot (`comparison.*`) and still rendered in the
 * league table's revenue column, so bringing it back is a render change only.
 *
 * `showAdr` is off for the portfolio summary (Adil, 2026-08-01). A portfolio ADR is an
 * average across units that price differently, so it describes no actual unit; per
 * property it is a real nightly rate the owner can act on. Same reason the count is
 * kept — it sums honestly, an average does not.
 */
const MetricGrid = ({ metrics, occupancyExtra, showAdr = true }) => (
  <div className="report-metric-grid">
    <div className="report-metric">
      <span className="report-metric-label">نسبة الإشغال</span>
      <strong className="report-metric-value">{formatPercent(metrics?.occupancy?.rate || 0)}</strong>
      <NightBreakdown occupancy={metrics?.occupancy} />
      {occupancyExtra}
    </div>

    <div className="report-metric">
      <span className="report-metric-label">إجمالي إيرادات الحجوزات</span>
      <strong className="report-metric-value">{formatSARGrouped(metrics?.revenue?.gross || 0)}</strong>
    </div>

    {showAdr && (
      <div className="report-metric">
        <span className="report-metric-label">متوسط السعر اليومي</span>
        <strong className="report-metric-value">{formatSARGrouped(metrics?.rates?.adr || 0)}</strong>
      </div>
    )}

    {/* RevPAR removed (Adil, 2026-07-30): for an owner reading their own report it is
        occupancy × ADR, both already above, and the label did not explain itself. Still
        in the snapshot (`rates.revPAR`) if it is ever wanted back. */}

    {/* Average stay length removed (Adil, 2026-08-01), portfolio and per-unit alike.
        Still in the snapshot (`bookings.averageStayLength`) if it is ever wanted back. */}
    <div className="report-metric">
      <span className="report-metric-label">عدد الحجوزات</span>
      <strong className="report-metric-value">{formatNumber(metrics?.bookings?.total || 0)}</strong>
    </div>
  </div>
);

/** Channel mix. Absent for a property with no bookings that month. */
const PlatformMix = ({ byPlatform }) => {
  if (!Array.isArray(byPlatform) || byPlatform.length === 0) return null;

  const total = byPlatform.reduce((sum, p) => sum + (p.grossRevenue || 0), 0);

  return (
    <div className="report-platform-mix">
      <h5>توزيع قنوات البيع</h5>
      <ul>
        {byPlatform.map(p => (
          <li key={p.platform}>
            <span className="platform-name">{getPlatformDisplayName(p.platform)}</span>
            <span className="platform-figures">
              {formatSARGrouped(p.grossRevenue || 0)}
              {' · '}{formatNumber(p.bookings || 0)}{' '}
              {arabicPlural(p.bookings || 0, 'حجز', 'حجزان', 'حجوزات')}
              {total > 0 ? ` · ${formatPercent(Math.round((p.grossRevenue / total) * 100))}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/*
 * Rules-based commentary is DEFERRED, not deleted (Adil, 2026-07-30). The builder
 * still produces `insights[]` on every snapshot — portfolio and per-property — so
 * re-enabling is a render change only, with no regeneration and no data loss. The
 * `.report-insights` styles are kept for the same reason.
 *
 * Same for the ownership disclosure line: removed from the page, and the fact it
 * describes still holds (`calculationBasis.ownershipPercentageApplied: false` is
 * on every snapshot). If ownership pro-rating ever ships, that sentence has to
 * come back before an owner sees a pro-rated figure.
 */

/**
 * @param {Object} report - the persisted snapshot
 * @param {'screen'|'print'} [variant='screen'] - `print` is the document form used
 *   by `pages/OwnerReportPrint.js`. It differs in exactly three ways, and the
 *   default keeps `/finance` byte-identical to what shipped:
 *     1. no inline header — `ReportMasthead` carries the identity on paper;
 *     2. the report-basis panel renders OPEN, because there is nothing to click on
 *        paper. This is the sanctioned fix for the note in MonthlyReports.css: CSS
 *        cannot force a `<details>` open, so whoever renders for print sets the
 *        attribute. Here that is React, not a headless browser;
 *     3. dates in Western digits, so one document uses one numeral system.
 */
const MonthlyReportView = ({ report, variant = 'screen' }) => {
  const payload = report?.payload;

  // A released report always has a payload (the release endpoint refuses a failed
  // snapshot), so this only guards a mid-fetch render.
  if (!payload) return null;

  const isPrint = variant === 'print';
  const summary = payload.executiveSummary || {};

  return (
    <article className={`monthly-report${isPrint ? ' is-print' : ''}`} dir="rtl">
      {!isPrint && (
        <header className="report-header">
          <div>
            <h3>تقرير الأداء الشهري</h3>
            <p className="report-period">{formatBusinessMonth(payload.periodMonth)}</p>
          </div>
          <div className="report-header-meta">
            <span>
              {formatNumber(summary.propertyCount || 0)}{' '}
              {arabicPlural(summary.propertyCount || 0, 'عقار', 'عقاران', 'عقارات')}
            </span>
            <span>أُنشئ في {formatDate(report.generatedAt)}</span>
          </div>
        </header>
      )}

      <section className="report-section report-summary">
        <h4>ملخص أداء لجميع الوحدات</h4>
        <MetricGrid metrics={summary.metrics} showAdr={false} />

        <div className="report-chart-block">
          <h5>اتجاه الإيرادات — آخر ٦ أشهر</h5>
          <RevenueTrendChart trend={summary.trend} currentMonth={payload.periodMonth} />
        </div>

        {/* The per-unit index, so the owner answers "which unit carried the month"
            without opening a section per property. Same figures, one screen. */}
        <PropertyLeagueTable properties={payload.properties} />

        {summary.best && summary.weakest && (
          <div className="report-extremes">
            <div className="report-extreme is-best">
              <span>أفضل أداء</span>
              <strong>{summary.best.name}</strong>
              <small>{formatSARGrouped(summary.best.grossRevenue)} · {formatPercent(summary.best.occupancyRate)}</small>
            </div>
            <div className="report-extreme is-weakest">
              <span>أضعف أداء</span>
              <strong>{summary.weakest.name}</strong>
              <small>{formatSARGrouped(summary.weakest.grossRevenue)} · {formatPercent(summary.weakest.occupancyRate)}</small>
            </div>
          </div>
        )}
      </section>

      {/* Altitude change. Everything above is the portfolio answered as one thing —
          including the league table, which is the summary's index, not a detail
          section. Everything below is one unit at a time. Every section already
          carries a 1px rule, so property→property and summary→detail looked
          identical; a named band is what makes the boundary mean something. */}
      {(payload.properties || []).length > 0 && (
        <div className="report-band">
          <h4>تفاصيل كل وحدة</h4>
        </div>
      )}

      {(payload.properties || []).map(property => (
        <section
          key={property.propertyId}
          className={`report-section report-property${property.hasActivity ? '' : ' is-idle'}`}
        >
          {/* Inline here, unlike the league table: a section heading has the whole
              width, and the code reads as part of the unit's title. */}
          <h4>{property.name} <UnitCode code={property.propertyCode} /></h4>

          <MetricGrid
            metrics={property.metrics}
            occupancyExtra={<PartialMonthNote coverage={property.coverage} latnDates={isPrint} />}
          />

          <PlatformMix byPlatform={property.metrics?.byPlatform} />

          <div className="report-chart-block">
            <h5>اتجاه الإيرادات — آخر ٦ أشهر</h5>
            <RevenueTrendChart trend={property.trend} currentMonth={payload.periodMonth} />
          </div>
        </section>
      ))}

      {/* Report basis — at the END, where report-wide notes belong. It previously sat
          between the summary and the property sections, which put a footnote in the
          middle of the boundary the band now marks.
          Collapsed, two lines, no explanation: findable by the owner who challenges a
          number, invisible to the owner who does not. Native <details> — no JS.
          NOTE: the ownership-percentage line was removed on request. That assumption
          (`calculationBasis.ownershipPercentageApplied: false`) is now disclosed
          NOWHERE in the UI, though it is still recorded on every snapshot. It has to
          come back before any pro-rated figure reaches an owner.
          On paper it renders OPEN — a collapsed panel prints as a triangle and a
          heading, which is how the first print sample lost its methodology entirely. */}
      <details className="report-basis" open={isPrint}>
        <summary>أساس التقرير</summary>
        <ul>
          <li>الإشغال محسوب على ليالي الشهر كاملة.</li>
          <li>الأرقام لا تتغير بعد إظهار التقرير.</li>
        </ul>
      </details>
    </article>
  );
};

export default MonthlyReportView;
